import { Field, ChipInput, ImageUpload, ListEditor, PillSelect } from "@/components/muse/inputs";

export const VOICE_TONES = ["Playful & witty", "Mentor & motivational", "Luxury & mysterious", "Bold & direct", "Soft & relatable"];
export const PLATFORMS = ["Instagram", "TikTok", "YouTube Shorts", "Facebook"];
export const GENERATORS = ["Higgsfield", "Kie", "Midjourney", "Kling", "Runway", "Other"];

export const EMPTY_MUSE = {
  name: "", age_range: "", look_description: "", body_build: "", never_rules: [], signature_outfits: [],
  signature_accessory: "", sets: [], voice_tone: null, voice_notes: "", niche: "", audience: "", platforms: [],
  generator: null, offers: [], face_reference_url: null, wizard_step: 1,
};

const keywordize = (v) => v.replace(/[^a-zA-Z]/g, "").toUpperCase();

export function StepIdentity({ m, set, ro }) {
  return (
    <div className="space-y-7">
      <Field label="Name *" helper="The name she goes by on every platform.">
        <input className="field" disabled={ro} value={m.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Kandii Kaine" data-testid="muse-name-input" />
      </Field>
      <Field label="Age range" helper="Keep it loose — it guides the generator, not a birthday.">
        <input className="field" disabled={ro} value={m.age_range} onChange={(e) => set("age_range", e.target.value)} placeholder="e.g. late 20s" data-testid="muse-age-range-input" />
      </Field>
      <Field label="Look description *" helper="Skin tone, face shape, eyes, hair, makeup. Be specific — this becomes her locked identity.">
        <textarea className="field min-h-[130px]" disabled={ro} value={m.look_description} onChange={(e) => set("look_description", e.target.value)}
          placeholder="e.g. deep brown skin, long dark body-wave hair, sculpted brows, winged eyeliner, glossy nude lip" data-testid="muse-look-description-input" />
      </Field>
      <Field label="Body build" helper="Describe proportions so her figure stays consistent.">
        <input className="field" disabled={ro} value={m.body_build} onChange={(e) => set("body_build", e.target.value)} placeholder="e.g. curvy-athletic: fuller hips, narrow waist, toned arms" data-testid="muse-body-build-input" />
      </Field>
      <Field label="Face reference" helper="Optional. A clear, front-facing portrait works best.">
        <ImageUpload value={m.face_reference_url} onChange={(v) => set("face_reference_url", v)} disabled={ro} testid="muse-face-reference" />
      </Field>
    </div>
  );
}

export function StepRules({ m, set, ro }) {
  return (
    <div className="space-y-7">
      <Field label="Never rules" helper="Hard no's the generator must respect. Press Enter to add each rule.">
        <ChipInput value={m.never_rules} onChange={(v) => set("never_rules", v)} disabled={ro} placeholder="e.g. No tattoos anywhere" testid="muse-never-rules" />
      </Field>
      <Field label="Signature outfits" helper="Looks she's known for — reuse them to build recognition.">
        <ListEditor items={m.signature_outfits} onChange={(v) => set("signature_outfits", v)} disabled={ro} testid="muse-outfits" addLabel="Add outfit"
          empty={{ name: "", description: "" }}
          fields={[{ key: "name", label: "Outfit name", placeholder: "e.g. Certified sweater" },
            { key: "description", label: "Description", multiline: true, placeholder: "e.g. oversized dusty-pink knit sweater with 'Certified' in white script" }]} />
      </Field>
      <Field label="Signature accessory" helper="One item that makes her instantly recognizable.">
        <input className="field" disabled={ro} value={m.signature_accessory} onChange={(e) => set("signature_accessory", e.target.value)} placeholder="e.g. black cat-eye sunglasses" data-testid="muse-accessory-input" />
      </Field>
    </div>
  );
}

export function StepSets({ m, set, ro }) {
  return (
    <Field label="Sets" helper="Recurring locations she films in. Detailed sets keep every scene on-brand.">
      <ListEditor items={m.sets} onChange={(v) => set("sets", v)} disabled={ro} testid="muse-sets" addLabel="Add set"
        empty={{ name: "", description: "", reference_image_url: null }}
        fields={[{ key: "name", label: "Set name", placeholder: "e.g. Certified Talk studio" },
          { key: "description", label: "Description", multiline: true, placeholder: "e.g. curved talk-show set, dark plum walls, pink neon sign, white marble desk" },
          { key: "reference_image_url", label: "Reference image (optional)",
            render: (it, up, i) => <ImageUpload compact value={it.reference_image_url} onChange={up} disabled={ro} testid={`muse-set-image-${i}`} /> }]} />
    </Field>
  );
}

export function StepVoice({ m, set, ro }) {
  return (
    <div className="space-y-7">
      <Field label="Voice tone" helper="How she sounds in captions and scripts.">
        <PillSelect options={VOICE_TONES} value={m.voice_tone} onChange={(v) => set("voice_tone", v)} disabled={ro} testid="muse-voice-tone" />
      </Field>
      <Field label="Voice notes" helper="Nuance the tone can't capture.">
        <textarea className="field min-h-[90px]" disabled={ro} value={m.voice_notes} onChange={(e) => set("voice_notes", e.target.value)} placeholder="e.g. spiritual mentor meets direct CEO; faith references subtle, never preachy" data-testid="muse-voice-notes-input" />
      </Field>
      <Field label="Niche" helper="What she's known for, in one line.">
        <input className="field" disabled={ro} value={m.niche} onChange={(e) => set("niche", e.target.value)} placeholder="e.g. AI money + mindset for women creators" data-testid="muse-niche-input" />
      </Field>
      <Field label="Audience" helper="Who she's talking to.">
        <input className="field" disabled={ro} value={m.audience} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. women 25–40 starting side hustles" data-testid="muse-audience-input" />
      </Field>
      <Field label="Platforms" helper="Select all that apply.">
        <PillSelect multi options={PLATFORMS} value={m.platforms} onChange={(v) => set("platforms", v)} disabled={ro} testid="muse-platforms" />
      </Field>
      <Field label="Generator" helper="The tool you use to render her.">
        <PillSelect options={GENERATORS} value={m.generator} onChange={(v) => set("generator", v)} disabled={ro} testid="muse-generator" />
      </Field>
    </div>
  );
}

export function StepOffers({ m, set, ro }) {
  return (
    <Field label="Offers" helper="What followers get when they comment the keyword. Keywords are letters only, auto-uppercased.">
      <ListEditor items={m.offers} onChange={(v) => set("offers", v)} disabled={ro} testid="muse-offers" addLabel="Add offer"
        empty={{ name: "", url: "", comment_keyword: "" }}
        fields={[{ key: "name", label: "Offer name", placeholder: "e.g. Free Starter Kit" },
          { key: "url", label: "URL", placeholder: "e.g. https://stan.store/kandiikaine" },
          { key: "comment_keyword", label: "Comment keyword", placeholder: "e.g. CLAIM", transform: keywordize }]} />
    </Field>
  );
}

export const STEPS = [
  { title: "Identity", sub: "Who she is, and how she looks.", C: StepIdentity },
  { title: "Rules & style", sub: "What she always wears — and never does.", C: StepRules },
  { title: "Sets", sub: "The worlds she lives in.", C: StepSets },
  { title: "Voice & niche", sub: "How she speaks, and to whom.", C: StepVoice },
  { title: "Offers", sub: "Where her audience goes next.", C: StepOffers },
];

export const toPayload = (m) => {
  const keys = Object.keys(EMPTY_MUSE);
  return Object.fromEntries(keys.map((k) => [k, m[k] ?? EMPTY_MUSE[k]]));
};
